import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { BaseService } from '@base/base.service';
import { AwardProgramImportStatus } from '@enums/award-program-import-status';
import { AwardProgramCriteriaDataRequest, AwardProgramCriteriaDataResponse, DownloadAwardProgramImportDataResponse, ListImportAwardProgramDataEventsRequest, ListImportAwardProgramDataEventsResponse } from '@models/awardPrograms/awardProgram';
import { AwardProgramResponse, AwardProgramFilterRequest, CreateEditAwardProgram, ImportAwardProgramDataRequest, ListAwardProgramCriteriasResponse, DownloadAwardProgramTemplateWorksheet } from '@models/awardPrograms/awardProgram';
import { AwardProgramProductTypeResponse, GetAwardProgramPanelResponse, GetPanelRequest } from '@models/awardPrograms/awardProgramPanel';
import { FileResponse } from '@models/settings/settings';
import { PaginatorItems } from '@models/table';
import { forkJoin, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AwardProgramService extends BaseService<AwardProgramResponse> {
  public readonly statusNames: Record<AwardProgramImportStatus, string> = {
    [AwardProgramImportStatus.Pending]: 'Pendente',
    [AwardProgramImportStatus.UnderValidation]: 'Em validação',
    [AwardProgramImportStatus.Processing]: 'Em processamento',
    [AwardProgramImportStatus.Success]: 'Processada',
    [AwardProgramImportStatus.Failed]: 'Erro'
  };

  constructor(private datePipe: DatePipe) {
    super()
    this.path = "gateway/AwardPrograms";
   }

   private createParam(awardProgramFilter: AwardProgramFilterRequest, pageNumber?: number, pageSize?: number): string {

    let query: string = "";

    if(awardProgramFilter?.active !== 0 && awardProgramFilter){
      query += `active=${awardProgramFilter.active}`
    }

    if(awardProgramFilter?.segment){
      query += `&segmentId=${awardProgramFilter.segment}`
    }

    if(awardProgramFilter?.productType){
      query += `&productTypeId=${awardProgramFilter.productType}`
    }

    if(awardProgramFilter && awardProgramFilter?.description){
      query += `&description=${awardProgramFilter.description}`
    }

    if(awardProgramFilter && awardProgramFilter?.createdBy.length > 0){
      query += `&createdBy=${awardProgramFilter.createdBy.join("&createdBy=")}`
    }

    if(awardProgramFilter && awardProgramFilter.lastModifiedBy.length > 0){
      query += `&lastModifiedBy=${awardProgramFilter.lastModifiedBy.join("&lastModifiedBy=")}`
    }

    if(awardProgramFilter && awardProgramFilter?.createdAt && awardProgramFilter?.createdAt?.startDate !== null){
      query += `&createdAt.startDate=${this.datePipe.transform(awardProgramFilter.createdAt.startDate, 'yyyy-MM-dd')}`
    }

    if(awardProgramFilter && awardProgramFilter?.createdAt && awardProgramFilter?.createdAt?.endDate !== null){
      query += `&createdAt.endDate=${this.datePipe.transform(awardProgramFilter.createdAt.endDate, 'yyyy-MM-dd')}`
    }

    if(awardProgramFilter && awardProgramFilter?.lastModifiedAt && awardProgramFilter?.lastModifiedAt?.startDate !== null){
      query += `&lastModifiedAt.startDate=${this.datePipe.transform(awardProgramFilter.lastModifiedAt.startDate, 'yyyy-MM-dd')}`
    }

    if(awardProgramFilter && awardProgramFilter?.lastModifiedAt && awardProgramFilter?.lastModifiedAt?.endDate !== null){
      query += `&lastModifiedAt.endDate=${this.datePipe.transform(awardProgramFilter.lastModifiedAt.endDate, 'yyyy-MM-dd')}`
    }

    if(pageNumber){
      query += `&pageNumber=${pageNumber}`
    }

    if(pageSize){
      query += `&pageSize=${pageSize}`
    }

    return query;
  }

  public filter(profileFilterRequest?: AwardProgramFilterRequest, pageNumber?: number, pageSize?: number): Observable<PaginatorItems<AwardProgramResponse>> {
    return this.http.get<PaginatorItems<AwardProgramResponse>>(`${this.api}${this.path}?${this.createParam(profileFilterRequest, pageNumber, pageSize)}`)
  }

  public save(awardProgram: CreateEditAwardProgram): Observable<any> {
    if(awardProgram.id)
      return this.http.put(`${this.api}${this.path}/${awardProgram.id}`, awardProgram);
    return this.http.post(`${this.api}${this.path}`, awardProgram);
  }

  public startImportingDataFor(awardProgramId: number ,payload: ImportAwardProgramDataRequest): Observable<any> {
    return super.post(payload, `${awardProgramId}/importReportData`)
  }

  public getById(id: number): Observable<CreateEditAwardProgram> {
    return this.get<CreateEditAwardProgram>(id);
  }

  public listCriteriasFor(awardProgramId: number) {
    return super.get<ListAwardProgramCriteriasResponse>(`${awardProgramId}/criterias`);
  }

  public downloadCriteriaTemplateFile() {
    return super.get<DownloadAwardProgramTemplateWorksheet>("templates/criteria/download");
  }

  public downloadDataTemplateFileFor(awardProgramId: number) {
    return super.get<DownloadAwardProgramTemplateWorksheet>(`templates/awardProgram/${awardProgramId}/download/current-data`)
  }

  public downloadDataHistoricTemplateFileFor(awardProgramId: number) {
    return super.get<DownloadAwardProgramTemplateWorksheet>(`templates/awardProgram/${awardProgramId}/download/historic-data`)
  }

  public getStatusDescription(status: AwardProgramImportStatus): string {
    return this.statusNames[status] || null;
  }

  public getAwardProgramImportEventLogs(id: number, pageNumber: number, pageSize: number, payload: ListImportAwardProgramDataEventsRequest) {
    payload.pageNumber = pageNumber;
    payload.pageSize = pageSize;

    return super.get<PaginatorItems<ListImportAwardProgramDataEventsResponse>>(`${id}/imports/events/?${this.createAwardProgramImportEventLogsParams(payload)}`)
  }

  public downloadAwardProgramImportEventLog(id: number, awardProgramId: number) {
    return super.get<DownloadAwardProgramImportDataResponse>(`${awardProgramId}/imports/events/download/${id}`);
  }

  public getPanel(request: GetPanelRequest): Observable<GetAwardProgramPanelResponse> {
    return this.http.get<GetAwardProgramPanelResponse>(`${this.api}${this.path}/getPanel?${this.createAwardProgramPanelParams(request)}`);
  }

  public getAwardProgramsByProductType(productTypeId: number): Observable<AwardProgramProductTypeResponse[]> {
    return this.http.get<AwardProgramProductTypeResponse[]>(`${this.api}${this.path}/productType/${productTypeId}`);
  }


  private createAwardProgramPanelParams(request: GetPanelRequest) {
    let query = "";

    query += `awardProgramId=${request.awardProgramId}`;
    if (!!request.dealerId) query += `&dealerId=${request.dealerId}`;
    if (!!request.economicGroupId) query += `&economicGroupId=${request.economicGroupId}`;

    return query;
  }


  private createAwardProgramImportEventLogsParams(payload: ListImportAwardProgramDataEventsRequest) {
    let query = "";

    query += `pageNumber=${payload.pageNumber}`;
    query += `&pageSize=${payload.pageSize}`;

    if (!!payload.createdBy) {
      query += `&createdBy=${payload.createdBy}`;
    }

    if (!!payload.createdAt && !!payload.createdAt.startDate && !!payload.createdAt.endDate) {
      query += `&createdAt.startDate=${payload.createdAt.startDate.toISOString()}&createdAt.endDate=${payload.createdAt.endDate.toISOString()}`;
    }

    if (!!payload.importType) {
      query += `&importType=${payload.importType}`;
    }

    if (!!payload.structure) {
      query += `&structure=${payload.structure}`;
    }

    if (!!payload.status) {
      query += `&status=${payload.status}`;
    }

    if (!!payload.dataSource) {
      query += `&dataSource=${payload.dataSource}`;
    }

    return query;
  }

  private createAwardProgramCriteriaDataParams(awardProgramCriteriaDataRequest: AwardProgramCriteriaDataRequest, identifierCode: string) {
    let query = "";

    query += `pageNumber=${awardProgramCriteriaDataRequest.pageNumber}`;
    query += `&pageSize=${awardProgramCriteriaDataRequest.pageSize}`;
    query += `&identifierCode=${identifierCode}`;

    if (!!awardProgramCriteriaDataRequest.id) {
      query += `&id=${awardProgramCriteriaDataRequest.id}`;
    }

    if (!!awardProgramCriteriaDataRequest.year) {
      query += `&year=${awardProgramCriteriaDataRequest.year}`;
    }

    if (!!awardProgramCriteriaDataRequest.description) {
      query += `&description=${awardProgramCriteriaDataRequest.description}`;
    }


    return query;
  }


  public getAwardProgramCriteriaData(awardProgramCriteriaDataRequest: AwardProgramCriteriaDataRequest, identifierCode: string): Observable<PaginatorItems<AwardProgramCriteriaDataResponse>> {
    return this.http.get<PaginatorItems<AwardProgramCriteriaDataResponse>>(`${this.api}${this.path}/getAwardProgramCriteriaData?${this.createAwardProgramCriteriaDataParams(awardProgramCriteriaDataRequest, identifierCode)}`);
  }

  public getAwardProgramCriteriaDataExportWorksheet(awardProgramCriteriaDataRequest: AwardProgramCriteriaDataRequest, identifierCode: string): Observable<FileResponse> {
    return this.http.get<FileResponse>(`${this.api}${this.path}/getAwardProgramCriteriaData/download/worksheet?${this.createAwardProgramCriteriaDataParams(awardProgramCriteriaDataRequest, identifierCode)}`);
  }

  public getAwardProgramCriteriaDataExporPdf(awardProgramCriteriaDataRequest: AwardProgramCriteriaDataRequest, identifierCode: string): Observable<Blob> {
    return this.http.get<Blob>(`${this.api}${this.path}/getAwardProgramCriteriaData/download/pdf?${this.createAwardProgramCriteriaDataParams(awardProgramCriteriaDataRequest, identifierCode)}`, {
      responseType: 'blob'
    } as any)  as unknown as Observable<Blob>;
  }

}
